import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getSettings } from '../services/SettingService';
import { getServiceGroups, getServiceItems, getServiceItemsByGroup } from '../services/ServiceService';
import { getPostCategories } from '../services/PostCategoryService';
import { getBanners } from '../services/BannerService';
import { getHomeData } from '../services/HomeService';
import { getTypicalCustomers } from '../services/TypicalCustomerService';
import { getBranches } from '../services/BranchService';
import EventService from '../services/EventService';
import { getAboutUs } from '../services/AboutUsService';

const DataContext = createContext();

export const useData = () => {
    const context = useContext(DataContext);
    if (!context) {
        throw new Error('useData must be used within a DataProvider');
    }
    return context;
};

export const DataProvider = ({ children }) => {
    // 1. Initialize state from cache if available to prevent loading flashes
    const getInitialState = (key, defaultVal) => {
        try {
            const cached = localStorage.getItem(`trunganh_cache_${key}`);
            if (cached) return JSON.parse(cached);
        } catch (e) {
            console.error('Error reading cache:', e);
        }
        return defaultVal;
    };

    const hasCache = useCallback(() => {
        return !!localStorage.getItem('trunganh_cache_settings') && 
               !!localStorage.getItem('trunganh_cache_banners');
    }, []);

    const [settings, setSettings] = useState(() => getInitialState('settings', null));
    const [serviceGroups, setServiceGroups] = useState(() => getInitialState('serviceGroups', []));
    const [serviceItems, setServiceItems] = useState(() => getInitialState('serviceItems', []));
    const [postCategories, setPostCategories] = useState(() => getInitialState('postCategories', []));
    const [banners, setBanners] = useState(() => getInitialState('banners', []));
    const [homeData, setHomeData] = useState(() => getInitialState('homeData', { services: [], news: [] }));
    const [typicalCustomers, setTypicalCustomers] = useState(() => getInitialState('typicalCustomers', []));
    const [branches, setBranches] = useState(() => getInitialState('branches', []));
    const [events, setEvents] = useState(() => getInitialState('events', []));
    const [aboutUs, setAboutUs] = useState(() => getInitialState('aboutUs', null));
    
    // Initial loading is false if we have cache
    const [loading, setLoading] = useState(!hasCache()); 
    const [error, setError] = useState(null);
    const fetchInProgress = useRef(false);
    const dataFetched = useRef(false);

    // Helper to save to cache
    const saveToCache = (key, data) => {
        try {
            if (data) {
                localStorage.setItem(`trunganh_cache_${key}`, JSON.stringify(data));
            }
        } catch (e) {
            console.warn('Cache save failed', e);
        }
    };

    const refreshData = useCallback(async (force = false) => {
        if (!force && (fetchInProgress.current || dataFetched.current)) return;

        fetchInProgress.current = true;
        
        // Use a direct check to localStorage to avoid stale closure state
        const settingsCache = localStorage.getItem('trunganh_cache_settings');
        const bannersCache = localStorage.getItem('trunganh_cache_banners');
        
        // Only show loading spinner if we don't have any cached settings/data yet
        if ((!settingsCache || !bannersCache) || force) {
            setLoading(true);
        }

        try {
            // 1. Fetch settings separately and FIRST so the logo/branding appears immediately
            getSettings().then(res => {
                const settingsData = Array.isArray(res) ? res[0] : (res?.data || res);
                if (settingsData) {
                    setSettings(settingsData);
                    saveToCache('settings', settingsData);
                }
            }).catch(err => console.error("Error fetching settings early", err));

            // 2. Fetch all other datasets in parallel
            // Optimization: Fetch all service items once instead of per group
            const [
                initialGroups,
                allItemsRes,
                categoriesRes,
                bannersRes,
                homeRes,
                typicalRes,
                branchesRes,
                eventsRes,
                aboutUsRes
            ] = await Promise.all([
                getServiceGroups(),
                getServiceItems(),
                getPostCategories(),
                getBanners(),
                getHomeData(),
                getTypicalCustomers(),
                getBranches(),
                EventService.getAll(),
                getAboutUs()
            ]);

            // Process Post Categories
            const categories = Array.isArray(categoriesRes) ? categoriesRes : (categoriesRes?.content || categoriesRes?.data || []);
            setPostCategories(categories);
            saveToCache('postCategories', categories);

            // Process Banners
            const bannerBody = bannersRes?.data || bannersRes;
            const finalBanners = Array.isArray(bannerBody) ? bannerBody : (bannerBody?.content || bannerBody?.data || []);
            setBanners(finalBanners);
            saveToCache('banners', finalBanners);

            // Process Home Data
            const finalHomeData = homeRes || { services: [], news: [] };
            setHomeData(finalHomeData);
            saveToCache('homeData', finalHomeData);

            // Process Typical Customers
            const finalTypical = typicalRes || [];
            setTypicalCustomers(finalTypical);
            saveToCache('typicalCustomers', finalTypical);

            // Process Branches
            const finalBranches = Array.isArray(branchesRes) ? branchesRes : (branchesRes?.data || []);
            setBranches(finalBranches);
            saveToCache('branches', finalBranches);

            // Process Events
            let rawEvents = [];
            if (Array.isArray(eventsRes)) {
                rawEvents = eventsRes;
            } else if (Array.isArray(eventsRes?.data)) {
                rawEvents = eventsRes.data;
            }
            const activeEvents = rawEvents
                .filter(e => e.isActive !== false && e.active !== false)
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setEvents(activeEvents);
            saveToCache('events', activeEvents);

            // Process About Us
            const aboutUsData = Array.isArray(aboutUsRes) ? aboutUsRes[0] : (aboutUsRes?.data || aboutUsRes);
            setAboutUs(aboutUsData);
            saveToCache('aboutUs', aboutUsData);

            // 3. Process Service Groups & Items (In-memory Optimization)
            // Filter and sort items early
            const activeItems = (Array.isArray(allItemsRes) ? allItemsRes : []).filter(i => i.isActive !== false);

            const activeGroups = (Array.isArray(initialGroups) ? initialGroups : []).filter(g => g.isActive !== false);

            // Enrich groups from the already fetched activeItems
            const enrichedGroups = activeGroups.map(group => {
                const groupId = group.id || group._id;
                const groupItems = activeItems.filter(item =>
                    item.serviceGroupId === groupId ||
                    item.serviceGroupName === group.name
                );

                // Fallback slug if missing
                let slug = group.slug;
                if (!slug) {
                    slug = (group.name || '').toLowerCase()
                        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                        .replace(/[đĐ]/g, "d")
                        .replace(/[^a-z0-9]/g, "-")
                        .replace(/-+/g, "-")
                        .replace(/^-|-$/g, "");
                }

                return {
                    ...group,
                    slug,
                    items: groupItems
                };
            });

            setServiceGroups(enrichedGroups);
            saveToCache('serviceGroups', enrichedGroups);
            setServiceItems(activeItems);
            saveToCache('serviceItems', activeItems);
            dataFetched.current = true;
            setError(null);
        } catch (err) {
            console.error("DataProvider: Error fetching common data", err);
            setError(err);
        } finally {
            setLoading(false);
            fetchInProgress.current = false;
        }
    }, []);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    const value = useMemo(() => ({
        settings,
        serviceGroups,
        serviceItems,
        postCategories,
        banners,
        homeData,
        typicalCustomers,
        branches,
        events,
        aboutUs,
        loading,
        error,
        refreshData
    }), [settings, serviceGroups, serviceItems, postCategories, banners, homeData, typicalCustomers, branches, events, aboutUs, loading, error, refreshData]);

    return (
        <DataContext.Provider value={value}>
            {children}
        </DataContext.Provider>
    );
};
